import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import BaseLayout from '../../admin/BaseLayout'
import { API_BASE_URL } from '../../config/api'
import LiveMatchDetailPage from '../../components/live/LiveMatchDetailPage'
import OracleLive from './OracleLive'
import {
  getHeatValue,
  getLeagueLabel,
  getMarketStateTheme,
  getMinute,
  getParticipants,
  getRisk,
  getScore,
  getTeamIndexValue,
  getTeamStatProfile,
  getTriggerLabel,
  isMatchInBucket,
} from '../../features/live-market/liveMarketUtils'
import { hydrateLiveMatch, sortDashboardMatches } from '../../features/live-market/liveIndexFallback'
import '../../features/live-market/LiveDashboard.css'

const tr = (value) => i18next.t(value, { defaultValue: value })
// Temporarily hide the Live Oracle in the frontend while keeping its implementation.
const SHOW_ORACLE_LIVE = false

const METRIC_COLUMNS = [
  { id: 'pi', label: 'Pressure Index', short: 'PI', type: 'index', tooltip: 'Pressure Index: intensidade ofensiva e pressao territorial da equipa.' },
  { id: 'ci', label: 'Control Index', short: 'CI', type: 'index', tooltip: 'Control Index: controlo do jogo atraves de posse, passes e dominio.' },
  { id: 'vi', label: 'Volatility Index', short: 'VI', type: 'index', tooltip: 'Volatility Index: instabilidade do jogo; valores altos indicam jogo aberto ou partido.' },
  { id: 'ri', label: 'Resistance Index', short: 'RI', type: 'index', tooltip: 'Resistance Index: capacidade de resistir a pressao e manter o jogo protegido.' },
  { id: 'xg', label: 'xG', symbol: 'xG', type: 'stat', tooltip: 'Golos esperados' },
  { id: 'possession', label: '% posse de bola', symbol: '%', type: 'stat', tooltip: 'Percentagem de posse de bola da equipa.' },
  { id: 'shotsOnTarget', label: 'Shots on target', symbol: 'ST', type: 'stat', tooltip: 'Remates enquadrados com a baliza.' },
  { id: 'shotsOffTarget', label: 'Shots off target', symbol: 'SF', type: 'stat', tooltip: 'Remates fora da baliza.' },
  { id: 'saves', label: 'Saves', symbol: 'SV', type: 'stat', tooltip: 'Defesas realizadas pelo guarda-redes.' },
  { id: 'dangerousAttacks', label: 'Dangerous attacks', symbol: 'DA', type: 'stat', tooltip: 'Ataques perigosos registados pela equipa.' },
  { id: 'corners', label: 'Corners', symbol: 'CK', type: 'stat', tooltip: 'Cantos conquistados pela equipa.' },
  { id: 'shotsTotal', label: 'Shots total', symbol: 'SH', type: 'stat', tooltip: 'Total de remates da equipa.' },
  { id: 'shotsInsideBox', label: 'Shots inside box', symbol: 'IB', type: 'stat', tooltip: 'Remates feitos dentro da area.' },
  { id: 'shotsOutsideBox', label: 'Shots outside box', symbol: 'OB', type: 'stat', tooltip: 'Remates feitos fora da area.' },
  { id: 'passes', label: 'Passes', symbol: 'PS', type: 'stat', tooltip: 'Total de passes da equipa.' },
  { id: 'bigChancesMissed', label: 'Big chances missed', symbol: 'BC', type: 'stat', tooltip: 'Grandes oportunidades falhadas.' },
  { id: 'duelsWon', label: 'Duels won', symbol: 'DW', type: 'stat', tooltip: 'Duelos ganhos pela equipa.' },
  { id: 'fouls', label: 'Fouls', symbol: 'FL', type: 'stat', tooltip: 'Faltas cometidas pela equipa.' },
  { id: 'yellowCards', label: 'Yellow cards', symbol: 'YC', type: 'stat', tooltip: 'Cartoes amarelos recebidos.' },
]

const DEFAULT_METRIC_IDS = [
  'pi',
  'ci',
  'vi',
  'ri',
  'xg',
  'possession',
  'shotsOnTarget',
  'shotsOffTarget',
  'saves',
  'dangerousAttacks',
  'corners',
]

const LIVE_NAVIGATION_CACHE_TTL_MS = 15 * 1000
const PRELIVE_NAVIGATION_CACHE_TTL_MS = 60 * 1000

const liveGamesPageCache = {
  liveMatches: [],
  realLiveMatches: [],
  preLiveMatches: [],
  preLiveWindow: null,
  activeFilter: 'live',
  selectedMatchId: null,
  liveFetchedAt: 0,
  preFetchedAt: 0,
}

const getLiveGamesPageCacheSnapshot = () => ({
  liveMatches: liveGamesPageCache.liveMatches,
  realLiveMatches: liveGamesPageCache.realLiveMatches,
  preLiveMatches: liveGamesPageCache.preLiveMatches,
  preLiveWindow: liveGamesPageCache.preLiveWindow,
  activeFilter: liveGamesPageCache.activeFilter || 'live',
  selectedMatchId: liveGamesPageCache.selectedMatchId,
  hasCachedLiveData:
    (Array.isArray(liveGamesPageCache.liveMatches) && liveGamesPageCache.liveMatches.length > 0) ||
    (Array.isArray(liveGamesPageCache.realLiveMatches) && liveGamesPageCache.realLiveMatches.length > 0),
  hasCachedPreLiveData:
    Array.isArray(liveGamesPageCache.preLiveMatches) && liveGamesPageCache.preLiveMatches.length > 0,
  hasFreshLiveData:
    liveGamesPageCache.liveFetchedAt > 0 &&
    Date.now() - liveGamesPageCache.liveFetchedAt < LIVE_NAVIGATION_CACHE_TTL_MS &&
    Array.isArray(liveGamesPageCache.liveMatches) &&
    liveGamesPageCache.liveMatches.length > 0,
  hasFreshPreLiveData:
    liveGamesPageCache.preFetchedAt > 0 &&
    Date.now() - liveGamesPageCache.preFetchedAt < PRELIVE_NAVIGATION_CACHE_TTL_MS &&
    Array.isArray(liveGamesPageCache.preLiveMatches) &&
    liveGamesPageCache.preLiveMatches.length > 0,
})

const updateLiveGamesPageCache = (partial) => {
  Object.assign(liveGamesPageCache, partial)
}

const SCORE_FILTERS = [
  { id: 'homeWinning', label: 'Casa Vencendo' },
  { id: 'awayWinning', label: 'Visitante Vencendo' },
  { id: 'draw', label: 'Empate' },
  { id: 'over15', label: 'Over 1.5' },
  { id: 'over25', label: 'Over 2.5' },
  { id: 'btts', label: 'Ambas marcam' },
]

const STAT_WINDOWS = [
  { id: 'total', label: 'TOTAL' },
  { id: 'firstHalf', label: '1T' },
  { id: 'secondHalf', label: '2T' },
  { id: 'last5', label: "5'" },
  { id: 'last10', label: "10'" },
]

const METRIC_TONE_THRESHOLDS = {
  pi: { green: 60, amber: 45 },
  ci: { green: 60, amber: 45 },
  vi: { green: 60, amber: 45 },
  ri: { green: 60, amber: 45 },
  xg: { green: 0.8, amber: 0.3 },
  possession: { green: 55, amber: 45 },
  shotsOnTarget: { green: 4, amber: 2 },
  shotsOffTarget: { green: 5, amber: 2 },
  saves: { green: 4, amber: 2 },
  dangerousAttacks: { green: 35, amber: 15 },
  corners: { green: 5, amber: 2 },
  shotsTotal: { green: 9, amber: 4 },
  shotsInsideBox: { green: 5, amber: 2 },
  shotsOutsideBox: { green: 5, amber: 2 },
  passes: { green: 350, amber: 180 },
  bigChancesMissed: { green: 2, amber: 1 },
  duelsWon: { green: 35, amber: 15 },
  fouls: { green: 12, amber: 6 },
  yellowCards: { green: 3, amber: 1 },
}

const buildDashboardMatches = (realMatches) => {
  return {
    matches: realMatches,
    mode: realMatches.length ? 'real' : 'empty',
  }
}

const StateBadge = ({ marketState }) => {
  const tone = getMarketStateTheme(marketState)
  return <span className={`live-state-badge live-tone-${tone}`}>{tr(marketState?.label || 'Unclear')}</span>
}

const TonePill = ({ children, tone = 'neutral', icon }) => (
  <span className={`live-signal-pill live-tone-${tone}`}>
    {icon && <i className={`bi ${icon}`} />}
    {children}
  </span>
)

const getMetricTone = (value, metricId) => {
  if (!Number.isFinite(value)) return 'muted'
  const thresholds = METRIC_TONE_THRESHOLDS[metricId] || { green: 60, amber: 40 }
  if (value >= thresholds.green) return 'green'
  if (value >= thresholds.amber) return 'amber'
  return 'red'
}

const MetricHeader = ({ column }) => (
  <span className="live-metric-heading" title={column.tooltip} aria-label={column.tooltip}>
    {column.symbol || column.short}
  </span>
)

const MetricValue = ({ value, type, metricId }) => {
  const numericValue = Number(value)
  const hasValue = Number.isFinite(numericValue)
  const displayValue = !hasValue ? '--' : type === 'stat' && Math.abs(numericValue % 1) > 0 ? numericValue.toFixed(2) : Math.round(numericValue)

  return (
    <span className={`live-metric-value live-metric-${getMetricTone(numericValue, metricId)}`}>
      {displayValue}
    </span>
  )
}

const ColumnSelector = ({ columns, visibleMetricIds, onToggleMetric, onClose }) => (
  <div className="live-column-menu" onClick={(event) => event.stopPropagation()}>
    <div className="live-column-menu-title">{i18next.t('liveDashboard.columns', { defaultValue: 'Colunas' })}</div>
    {columns.map((column) => {
      const active = visibleMetricIds.includes(column.id)
      return (
        <button
          type="button"
          className="live-column-option"
          key={column.id}
          onClick={() => onToggleMetric(column.id)}
        >
          <span>{tr(column.label)}</span>
          <strong className={active ? 'is-on' : ''}>{active ? 'ON' : 'OFF'}</strong>
        </button>
      )
    })}
    <button type="button" className="live-secondary-button w-100 mt-2" onClick={onClose}>
      {i18next.t('Close', { defaultValue: 'Fechar' })}
    </button>
  </div>
)

const ScoreFilterMenu = ({ filters, activeFilter, onSelectFilter, onClose }) => (
  <div className="live-column-menu live-filter-menu" onClick={(event) => event.stopPropagation()}>
    <div className="live-column-menu-title">{i18next.t('liveDashboard.filters', { defaultValue: 'Filtros' })}</div>
    {filters.map((filter) => {
      const active = activeFilter === filter.id
      return (
        <button
          type="button"
          className="live-column-option"
          key={filter.id}
          onClick={() => onSelectFilter(active ? null : filter.id)}
        >
          <span>{tr(filter.label)}</span>
          <strong className={active ? 'is-on' : ''}>{active ? 'ON' : 'OFF'}</strong>
        </button>
      )
    })}
    <button type="button" className="live-secondary-button w-100 mt-2" onClick={onClose}>
      {i18next.t('Close', { defaultValue: 'Fechar' })}
    </button>
  </div>
)

const KpiCard = ({ label, value, note, icon, tone = 'green' }) => (
  <div className="live-kpi-card">
    <div className="d-flex justify-content-between align-items-start">
      <div className="live-kpi-content">
        <div className="live-kpi-label">{label}</div>
        <div className="live-kpi-value">{value}</div>
        {note && <div className={`live-kpi-note live-text-${tone}`}>{note}</div>}
      </div>
      {icon && <i className={`bi ${icon} live-tone-${tone}`} />}
    </div>
  </div>
)

const LiveTable = ({
  matches,
  selectedMatch,
  onSelectMatch,
  onOpenMatch,
  onOpenRadar,
  t,
  totalCount,
  activeFilter,
  visibleMetricIds,
  onToggleMetric,
  isColumnMenuOpen,
  onToggleColumnMenu,
  onCloseColumnMenu,
  statWindow,
}) => {
  const visibleColumns = METRIC_COLUMNS.filter((column) => visibleMetricIds.includes(column.id))

  return (
    <div className="live-table-panel">
      <div className="live-table-header">
        <span className="live-status-dot" />
        <span className="live-panel-label">
          {activeFilter === 'pre'
            ? t('liveDashboard.preOverview', 'OVERVIEW DOS JOGOS PRE LIVE')
            : t('liveDashboard.marketOverview', 'OVERVIEW DOS MERCADOS LIVE')}
        </span>
        <span className="live-mini-label ms-auto">
          {totalCount || matches.length}{' '}
          {activeFilter === 'pre'
            ? t('liveDashboard.matchesUpcoming', 'JOGOS AGENDADOS')
            : t('liveDashboard.matchesLive', 'PARTIDAS AO VIVO')}
        </span>
        <div className="live-column-control">
          <button
            type="button"
            className="live-icon-button"
            onClick={onToggleColumnMenu}
            title={t('liveDashboard.columns', 'Colunas')}
          >
            <span>{t('liveDashboard.columns', 'Colunas')}</span>
          </button>
          {isColumnMenuOpen && (
            <ColumnSelector
              columns={METRIC_COLUMNS}
              visibleMetricIds={visibleMetricIds}
              onToggleMetric={onToggleMetric}
              onClose={onCloseColumnMenu}
            />
          )}
        </div>
      </div>

      <div className="live-table-scroll">
        <table className="live-table live-table-team-mode">
          <thead>
            <tr>
              <th>{t('liveDashboard.table.league', 'Liga')}</th>
              <th>{t('liveDashboard.table.minute', 'Min')}</th>
              <th>{t('liveDashboard.table.match', 'Match')}</th>
              <th>{t('liveDashboard.table.score', 'Score')}</th>
              <th>{t('liveDashboard.table.marketState', 'Estado de Mercado')}</th>
              {visibleColumns.map((column) => (
                <th className="live-metric-th" key={column.id}>
                  <MetricHeader column={column} />
                </th>
              ))}
              <th>{t('liveDashboard.table.risk', 'Risco')}</th>
              <th>{t('liveDashboard.table.trigger', 'Trigger')}</th>
              <th>{t('liveDashboard.table.actions', 'Acoes')}</th>
            </tr>
          </thead>
          <tbody>
            {matches.map((match) => {
              const { homeName, awayName } = getParticipants(match)
              const score = getScore(match)
              const risk = getRisk(match)
              const rows = [
                { side: 'home', name: homeName, score: score.home },
                { side: 'away', name: awayName, score: score.away },
              ]

              return rows.map((team, index) => {
                const statProfile = getTeamStatProfile(match, team.side, statWindow)
                const isFirstTeamRow = index === 0

                return (
                  <tr
                    className={`${selectedMatch?.id === match.id ? 'selected' : ''} ${isFirstTeamRow ? 'live-match-top-row' : 'live-match-bottom-row'}`}
                    key={`${match.id}-${team.side}`}
                    onClick={() => {
                      onSelectMatch(match)
                      onOpenMatch(match)
                    }}
                  >
                    {isFirstTeamRow && (
                      <>
                        <td className="live-league-cell" rowSpan={2}>{getLeagueLabel(match)}</td>
                        <td className="live-minute" rowSpan={2}>{getMinute(match)}</td>
                      </>
                    )}
                    <td className="live-match-cell">
                      <span className={`live-team-side-dot live-team-${team.side}`} />
                      <span>{team.name}</span>
                    </td>
                    <td className="live-score-cell">{team.score}</td>
                    {isFirstTeamRow && (
                      <td className="live-state-cell" rowSpan={2}><StateBadge marketState={match.marketState} /></td>
                    )}
                    {visibleColumns.map((column) => {
                      const value = column.type === 'index'
                        ? getTeamIndexValue(match, team.side, column.id)
                        : statProfile[column.id]
                      return (
                        <td className="live-metric-cell" key={`${match.id}-${team.side}-${column.id}`}>
                          <MetricValue metricId={column.id} value={value} type={column.type} />
                        </td>
                      )
                    })}
                    {isFirstTeamRow && (
                      <>
                        <td rowSpan={2}><TonePill tone={risk.tone}>{risk.label}</TonePill></td>
                        <td className="live-trigger-cell" rowSpan={2}>{tr(getTriggerLabel(match))}</td>
                        <td rowSpan={2}>
                          <div className="live-row-actions" onClick={(event) => event.stopPropagation()}>
                            <button
                              type="button"
                              className="live-action-button live-action-terminal"
                              title={t('liveDashboard.actions.openTerminal', 'Abrir terminal')}
                              onClick={() => onOpenMatch(match)}
                            >
                              <span>{t('liveDashboard.actions.openTerminalShort', 'VER')}</span>
                            </button>
                            {activeFilter === 'live' && (
                              <>
                                <button
                                  type="button"
                                  className="live-action-button live-radar-action"
                                  title={t('liveDashboard.actions.openRadar', 'Abrir radar em nova janela')}
                                  aria-label={t('liveDashboard.actions.openRadar', 'Abrir radar em nova janela')}
                                  onClick={() => onOpenRadar(match)}
                                >
                                  <span>{t('liveDashboard.actions.openRadarShort', 'RADAR')}</span>
                                </button>
                                <a
                                  className="live-action-button live-action-trade"
                                  href="https://quantumodds.xyz/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={t('liveDashboard.actions.openTrade', 'Abrir QuantumOdds')}
                                  aria-label={t('liveDashboard.actions.openTrade', 'Abrir QuantumOdds')}
                                >
                                  <span>{t('liveDashboard.actions.tradeShort', 'TRADE')}</span>
                                </a>
                              </>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                )
              })
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const getMobileMetricTone = (column, value) => {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue) || numericValue <= 0) return 'muted'
  if (column.type === 'index') return getMetricTone(numericValue, column.id)
  if (column.id === 'possession') {
    if (numericValue >= 55) return 'green'
    if (numericValue >= 45) return 'amber'
    return 'red'
  }
  if (column.id === 'xg') {
    return getMetricTone(numericValue, column.id)
  }
  return getMetricTone(numericValue, column.id)
}

const LiveMobileCards = ({ matches, selectedMatch, onSelectMatch, onOpenMatch, onOpenRadar, activeFilter, statWindow, visibleMetricIds, t }) => {
  const visibleColumns = METRIC_COLUMNS.filter((column) => visibleMetricIds.includes(column.id))

  return (
    <div className="live-mobile-list">
      {matches.map((match) => {
      const { homeName, awayName } = getParticipants(match)
      const score = getScore(match)
      const risk = getRisk(match)
      const teams = [
        { side: 'home', name: homeName, score: score.home },
        { side: 'away', name: awayName, score: score.away },
      ]

        return (
          <article
            className={`live-mobile-card ${selectedMatch?.id === match.id ? 'selected' : ''}`}
            key={match.id}
            onClick={() => {
              onSelectMatch(match)
              onOpenMatch(match)
            }}
          >
          <div className="live-mobile-card-head">
            <div>
              <div className="live-mobile-league">{getLeagueLabel(match)}</div>
              <div className="live-mobile-minute">{getMinute(match)}</div>
            </div>
            <StateBadge marketState={match.marketState} />
          </div>

          <div className="live-mobile-scoreline">
            <span>{homeName}</span>
            <strong>{score.label}</strong>
            <span>{awayName}</span>
          </div>

          <div className="live-mobile-teams">
            {teams.map((team) => {
              const stats = getTeamStatProfile(match, team.side, statWindow)

              return (
                <div className="live-mobile-team-row" key={`${match.id}-${team.side}`}>
                  <div className="live-mobile-team-name">
                    <span className={`live-team-side-dot live-team-${team.side}`} />
                    <span>{team.name}</span>
                    <strong>{team.score}</strong>
                  </div>
                  <div className="live-mobile-metrics">
                    {visibleColumns.map((column) => {
                      const value = column.type === 'index'
                        ? getTeamIndexValue(match, team.side, column.id)
                        : stats[column.id]
                      const tone = getMobileMetricTone(column, value)

                      return (
                        <div className={`live-mobile-metric live-mobile-metric-${tone}`} key={`${team.side}-${column.id}`}>
                          <span>{column.symbol || column.short}</span>
                          <MetricValue metricId={column.id} value={value} type={column.type} />
                        </div>
                      )
                    })}
                    {!visibleColumns.length && (
                      <div className="live-mobile-metric live-mobile-metric-muted">
                        <span>--</span>
                        <MetricValue metricId="empty" value={null} type="stat" />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <div className="live-mobile-card-foot">
            <div>
              <span className="live-mobile-foot-label">{t('liveDashboard.table.risk', 'Risco')}</span>
              <TonePill tone={risk.tone}>{risk.label}</TonePill>
            </div>
            <div>
              <span className="live-mobile-foot-label">{t('liveDashboard.table.trigger', 'Trigger')}</span>
              <strong>{tr(getTriggerLabel(match))}</strong>
            </div>
            {activeFilter === 'live' && (
              <button
                type="button"
                className="live-mobile-radar-button"
                onClick={(event) => {
                  event.stopPropagation()
                  onOpenRadar(match)
                }}
              >
                <i className="bi bi-window-stack" />
                {t('liveDashboard.actions.openRadarShort', 'RADAR')}
              </button>
            )}
          </div>
          </article>
        )
      })}
    </div>
  )
}

const LiveGames = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { matchId } = useParams()
  const liveViewParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const requestedOracleLive = SHOW_ORACLE_LIVE && liveViewParams.get('view') === 'oracle-live'
  const requestedOracleMatchKey = liveViewParams.get('match')
  const isRadarWindow = Boolean(
    matchId && typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('radar') === '1'
  )
  const initialCache = useRef(getLiveGamesPageCacheSnapshot()).current
  const [liveMatches, setLiveMatches] = useState(() => initialCache.liveMatches)
  const [realLiveMatches, setRealLiveMatches] = useState(() => initialCache.realLiveMatches)
  const [preLiveMatches, setPreLiveMatches] = useState(() => initialCache.preLiveMatches)
  const [preLiveWindow, setPreLiveWindow] = useState(() => initialCache.preLiveWindow)
  const [, setUsingHybridLiveData] = useState(false)
  const [selectedMatchId, setSelectedMatchId] = useState(() => initialCache.selectedMatchId)
  const [liveLoading, setLiveLoading] = useState(() => !initialCache.hasCachedLiveData)
  const [preLoading, setPreLoading] = useState(() => false)
  const [error, setError] = useState(null)
  const [preError, setPreError] = useState(null)
  const [activeFilter, setActiveFilter] = useState(() => requestedOracleLive ? 'oracle-live' : (!SHOW_ORACLE_LIVE && initialCache.activeFilter === 'oracle-live' ? 'live' : initialCache.activeFilter))
  const [statWindow, setStatWindow] = useState('total')
  const [scoreFilter, setScoreFilter] = useState(null)
  const [isScoreFilterOpen, setIsScoreFilterOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [visibleMetricIds, setVisibleMetricIds] = useState(DEFAULT_METRIC_IDS)
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false)
  const liveFetchedAtRef = useRef(liveGamesPageCache.liveFetchedAt || 0)
  const preFetchedAtRef = useRef(liveGamesPageCache.preFetchedAt || 0)
  const hasInitialCachedLiveDataRef = useRef(initialCache.hasCachedLiveData)
  const hasInitialFreshLiveDataRef = useRef(initialCache.hasFreshLiveData)
  const hasInitialCachedPreLiveDataRef = useRef(initialCache.hasCachedPreLiveData)
  const hasInitialFreshPreLiveDataRef = useRef(initialCache.hasFreshPreLiveData)
  const preLiveCountRef = useRef(preLiveMatches.length)

  useEffect(() => {
    preLiveCountRef.current = preLiveMatches.length
  }, [preLiveMatches.length])

  useEffect(() => {
    if (requestedOracleLive) setActiveFilter('oracle-live')
  }, [requestedOracleLive])

  useEffect(() => {
    let isCancelled = false

    const fetchLiveGames = async ({ showLoading = false } = {}) => {
      try {
        if (showLoading) {
          setLiveLoading(true)
        }
        const liveResult = await axios.get(`${API_BASE_URL}/api/live`, {
          params: isRadarWindow ? { refresh: true } : undefined,
        })

        let nextSelectedMatchId = null
        let nextError = null
        const providerMatches = liveResult.data?.data || (Array.isArray(liveResult.data) ? liveResult.data : [])
        const matches = sortDashboardMatches(providerMatches.map(hydrateLiveMatch))
        const dashboardData = buildDashboardMatches(matches)

        if (isCancelled) return

        setRealLiveMatches(matches)
        setUsingHybridLiveData(dashboardData.mode === 'hybrid')
        setLiveMatches(dashboardData.matches)
        nextSelectedMatchId = matches[0]?.id || null
        liveFetchedAtRef.current = Date.now()
        setError(nextError)
        setSelectedMatchId((current) => current || nextSelectedMatchId)
        updateLiveGamesPageCache({
          liveMatches: dashboardData.matches,
          realLiveMatches: matches,
          selectedMatchId: nextSelectedMatchId,
          liveFetchedAt: liveFetchedAtRef.current,
        })
      } catch (err) {
        if (isCancelled) return
        const status = err?.response?.status
        const overloadMessage =
          status === 429 || status === 503
            ? err?.response?.data?.message || 'De momento existem muitos utilizadores. Tenta novamente dentro de alguns minutos.'
            : null

        setLiveMatches((currentMatches) => {
          if (currentMatches.length) {
            setError(null)
            return currentMatches
          }
          setError(overloadMessage || 'Os mercados live estão temporariamente indisponíveis. Tenta novamente dentro de alguns minutos.')
          setUsingHybridLiveData(false)
          setRealLiveMatches([])
          return []
        })
      } finally {
        if (!isCancelled) {
          setLiveLoading(false)
        }
      }
    }

    if (isRadarWindow || !hasInitialFreshLiveDataRef.current) {
      fetchLiveGames({ showLoading: !hasInitialCachedLiveDataRef.current })
    }
    const interval = setInterval(fetchLiveGames, isRadarWindow ? 12000 : 20000)
    return () => {
      isCancelled = true
      clearInterval(interval)
    }
  }, [isRadarWindow])

  useEffect(() => {
    if (activeFilter !== 'pre') return undefined

    let isCancelled = false

    const fetchPreLiveGames = async ({ showLoading = false } = {}) => {
      try {
        if (showLoading) {
          setPreLoading(true)
        }

        const response = await axios.get(`${API_BASE_URL}/api/live/pre`)
        if (isCancelled) return

        const fixtures = response.data?.data || []
        const window = response.data?.window || null
        preFetchedAtRef.current = Date.now()

        setPreLiveMatches(fixtures)
        setPreLiveWindow(window)
        setPreError(null)
        setSelectedMatchId((current) => current || fixtures[0]?.id || null)
        updateLiveGamesPageCache({
          preLiveMatches: fixtures,
          preLiveWindow: window,
          preFetchedAt: preFetchedAtRef.current,
        })
      } catch (preLiveError) {
        if (isCancelled) return
        if (!preLiveCountRef.current) {
          setPreError('Os jogos pré-live estão temporariamente indisponíveis. Tenta novamente dentro de alguns minutos.')
        }
      } finally {
        if (!isCancelled) {
          setPreLoading(false)
        }
      }
    }

    if (!hasInitialFreshPreLiveDataRef.current) {
      fetchPreLiveGames({
        showLoading: !hasInitialCachedPreLiveDataRef.current && !preLiveCountRef.current,
      })
    }
    const interval = setInterval(fetchPreLiveGames, 60000)

    return () => {
      isCancelled = true
      clearInterval(interval)
    }
  }, [activeFilter])

  useEffect(() => {
    if (!matchId) return
    setSelectedMatchId(String(matchId))
  }, [matchId])

  useEffect(() => {
    updateLiveGamesPageCache({
      liveMatches,
      realLiveMatches,
      preLiveMatches,
      preLiveWindow,
      activeFilter,
      selectedMatchId,
      liveFetchedAt: liveFetchedAtRef.current,
      preFetchedAt: preFetchedAtRef.current,
    })
  }, [activeFilter, liveMatches, realLiveMatches, preLiveMatches, preLiveWindow, selectedMatchId])

  const displayedMatches = useMemo(() => {
    if (activeFilter === 'pre') return preLiveMatches
    return liveMatches
  }, [activeFilter, liveMatches, preLiveMatches])

  const filteredMatches = useMemo(() => {
    const query = search.trim().toLowerCase()

    return displayedMatches
      .filter((match) => isMatchInBucket(match, activeFilter))
      .filter((match) => {
        if (!scoreFilter) return true
        const score = getScore(match)
        const homeGoals = Number(score.home) || 0
        const awayGoals = Number(score.away) || 0
        const totalGoals = homeGoals + awayGoals
        if (scoreFilter === 'homeWinning') return homeGoals > awayGoals
        if (scoreFilter === 'awayWinning') return awayGoals > homeGoals
        if (scoreFilter === 'draw') return homeGoals === awayGoals
        if (scoreFilter === 'over15') return totalGoals >= 2
        if (scoreFilter === 'over25') return totalGoals >= 3
        if (scoreFilter === 'btts') return homeGoals > 0 && awayGoals > 0
        return true
      })
      .filter((match) => {
        if (!query) return true
        const { homeName, awayName } = getParticipants(match)
        return `${homeName} ${awayName} ${getLeagueLabel(match)}`.toLowerCase().includes(query)
      })
  }, [activeFilter, displayedMatches, scoreFilter, search])

  const selectedMatch = filteredMatches.find((match) => match.id === selectedMatchId) || filteredMatches[0] || null
  useEffect(() => {
    if (!filteredMatches.length) return
    const hasCurrentSelection = filteredMatches.some((match) => String(match.id) === String(selectedMatchId))
    if (!hasCurrentSelection) {
      setSelectedMatchId(filteredMatches[0].id)
    }
  }, [filteredMatches, selectedMatchId])
  const activeMatches = displayedMatches.filter((match) => isMatchInBucket(match, 'live'))
  const realActiveMatches = realLiveMatches.filter((match) => isMatchInBucket(match, 'live'))
  const realTimeLiveMatches = realActiveMatches.length ? realActiveMatches : activeMatches
  const isRealFeedAvailable = realActiveMatches.length > 0
  const activeMatchCount =
    activeFilter === 'pre'
      ? preLiveMatches.length
      : realTimeLiveMatches.length
  const heatValue = activeFilter === 'pre'
    ? 0
    : isRealFeedAvailable
      ? getHeatValue(realActiveMatches)
      : getHeatValue(activeMatches)
  const handleToggleMetric = (metricId) => {
    setVisibleMetricIds((current) => {
      if (current.includes(metricId)) {
        return current.filter((id) => id !== metricId)
      }
      return [...current, metricId]
    })
  }

  const handleViewChange = (view) => {
    if (view === 'oracle-live' && !SHOW_ORACLE_LIVE) return
    setActiveFilter(view)
    if (view === 'oracle-live') {
      navigate('/live?view=oracle-live', { replace: true })
    } else if (requestedOracleLive) {
      navigate('/live', { replace: true })
    }
  }

  const handleOpenMatch = (match) => {
    if (!match?.id) return
    setSelectedMatchId(match.id)
    navigate(`/live/${match.id}`)
  }

  const handleOpenRadar = (match) => {
    if (!match?.id || typeof window === 'undefined') return

    const width = Math.min(1240, Math.max(920, Math.round(window.screen.availWidth * 0.72)))
    const height = Math.min(860, Math.max(680, Math.round(window.screen.availHeight * 0.82)))
    const openWindows = Number(sessionStorage.getItem('filtermarket_radar_window_count') || 0)
    const offset = (openWindows % 6) * 28
    const left = Math.max(0, Math.round((window.screen.availWidth - width) / 2) + offset)
    const top = Math.max(0, Math.round((window.screen.availHeight - height) / 2) + offset)
    const radarUrl = `${window.location.origin}/live/${match.id}?radar=1`
    const radarWindow = window.open(
      radarUrl,
      `filtermarket-radar-${match.id}`,
      `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    )

    if (radarWindow) {
      sessionStorage.setItem('filtermarket_radar_window_count', String(openWindows + 1))
      radarWindow.focus()
    }
  }

  const detailMatch = useMemo(() => {
    if (!matchId) return null
    return [...liveMatches, ...preLiveMatches, ...realLiveMatches].find((match) => String(match.id) === String(matchId)) || null
  }, [liveMatches, matchId, preLiveMatches, realLiveMatches])

  const displayError = activeFilter === 'pre' ? preError : error
  const isLoading =
    activeFilter === 'pre'
      ? preLoading && !preLiveMatches.length
      : liveLoading && !liveMatches.length && !realLiveMatches.length

  if (matchId) {
    const isRadarMode = isRadarWindow
    const handleRadarBack = () => {
      if (!isRadarMode || typeof window === 'undefined') {
        navigate('/live')
        return
      }
      window.close()
      window.setTimeout(() => {
        if (!window.closed) navigate('/live')
      }, 120)
    }
    const detailContent = liveLoading && !detailMatch ? (
      <div className="live-table-panel live-empty-state">{t('liveDashboard.loading', 'A sincronizar mercados live...')}</div>
    ) : detailMatch ? (
      <LiveMatchDetailPage match={detailMatch} onBack={handleRadarBack} radarMode={isRadarMode} />
    ) : (
      <div className="match-detail-shell">
        <button type="button" className="match-back-button" onClick={handleRadarBack}>
          <i className={`bi ${isRadarMode ? 'bi-x-lg' : 'bi-arrow-left'}`} />
          {isRadarMode ? t('Close', 'Fechar') : t('Overview', 'Visão geral')}
        </button>
        <div className="match-detail-empty">{t('liveDashboard.matchNotFound', 'Jogo nao encontrado no feed live atual.')}</div>
      </div>
    )

    if (isRadarMode) {
      return <div className="radar-window-root">{detailContent}</div>
    }

    return (
      <BaseLayout contentClassName="app-container-live-full">
        <div className="live-dashboard-shell">
          {detailContent}
        </div>
      </BaseLayout>
    )
  }

  return (
    <BaseLayout contentClassName="app-container-live-full">
      <div className="live-dashboard-shell">
        <div className="live-dashboard-header">
          <div className="live-dashboard-title">{t('liveDashboard.title', 'JOGOS AO VIVO')}</div>
          <div className="live-segmented" role="tablist" aria-label="Live match status">
            <button type="button" className={activeFilter === 'pre' ? 'active' : ''} onClick={() => handleViewChange('pre')}>
              {t('liveDashboard.tabs.pre', 'Jogos Pre Live')}
            </button>
            <button type="button" className={activeFilter === 'live' ? 'active' : ''} onClick={() => handleViewChange('live')}>
              {t('liveDashboard.tabs.live', 'Jogos a Decorrer')}
            </button>
            {SHOW_ORACLE_LIVE && <button type="button" className={activeFilter === 'oracle-live' ? 'active' : ''} onClick={() => handleViewChange('oracle-live')}>
              Oráculo Live
            </button>}
          </div>
        </div>

        {SHOW_ORACLE_LIVE && activeFilter === 'oracle-live' ? (
          <OracleLive initialMatchKey={requestedOracleMatchKey} />
        ) : (
          <>
        <div className="live-dashboard-controls">
          <div className="live-search">
            <span className="live-search-icon" aria-hidden="true" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('liveDashboard.search', 'Buscar')}
            />
          </div>

          <div className="live-filter-control">
            <button
              type="button"
              className={`live-filter-button ${scoreFilter ? 'has-filter' : ''}`}
              onClick={() => setIsScoreFilterOpen((current) => !current)}
            >
              <span>{scoreFilter ? tr(SCORE_FILTERS.find((filter) => filter.id === scoreFilter)?.label || '') : t('liveDashboard.filters', 'Filtros')}</span>
            </button>
            {isScoreFilterOpen && (
              <ScoreFilterMenu
                filters={SCORE_FILTERS}
                activeFilter={scoreFilter}
                onSelectFilter={(filterId) => {
                  setScoreFilter(filterId)
                  setIsScoreFilterOpen(false)
                }}
                onClose={() => setIsScoreFilterOpen(false)}
              />
            )}
          </div>

          <div className="live-mobile-columns-control">
            <button
              type="button"
              className="live-filter-button"
              onClick={() => setIsColumnMenuOpen((current) => !current)}
            >
              <span>{t('liveDashboard.columns', 'Colunas')}</span>
            </button>
            {isColumnMenuOpen && (
              <ColumnSelector
                columns={METRIC_COLUMNS}
                visibleMetricIds={visibleMetricIds}
                onToggleMetric={handleToggleMetric}
                onClose={() => setIsColumnMenuOpen(false)}
              />
            )}
          </div>

          <div className="live-time-filter">
            {STAT_WINDOWS.map((windowOption) => (
              <button
                className={statWindow === windowOption.id ? 'active' : ''}
                key={windowOption.id}
                onClick={() => setStatWindow(windowOption.id)}
                type="button"
                title={
                  windowOption.id === 'total'
                    ? 'Estatisticas totais do jogo ate ao momento'
                    : windowOption.id === 'firstHalf'
                      ? 'Estatisticas da primeira parte'
                      : windowOption.id === 'secondHalf'
                        ? 'Estatisticas da segunda parte'
                        : windowOption.id === 'last5'
                          ? 'Estatisticas dos ultimos 5 minutos'
                          : 'Estatisticas dos ultimos 10 minutos'
                }
              >
                {windowOption.label}
              </button>
            ))}
          </div>

          <KpiCard
            label={activeFilter === 'pre' ? t('liveDashboard.kpis.preLive', 'Jogos Pre Live') : t('liveDashboard.kpis.active', 'Partidas Ativas')}
            value={activeMatchCount}
            note={activeFilter === 'pre' ? `${preLiveWindow?.rangeStartDate || '--'} -> ${preLiveWindow?.rangeEndDate || '--'}` : 'Total live agora'}
            icon="bi-graph-up-arrow"
          />
          <KpiCard
            label={t('liveDashboard.kpis.heat', 'Heat do Mercado')}
            value={heatValue}
            note={activeFilter === 'pre' ? 'Janela pre live' : 'Alta atividade'}
            icon="bi-fire"
            tone="red"
          />
        </div>

        <div className="live-dashboard-grid">
          {isLoading ? (
            <div className="live-table-panel live-empty-state">{t('liveDashboard.loading', 'A sincronizar mercados live...')}</div>
          ) : displayError ? (
            <div className="live-table-panel live-error-state">{displayError}</div>
          ) : filteredMatches.length ? (
            <>
              <LiveTable
                matches={filteredMatches}
                selectedMatch={selectedMatch}
                onSelectMatch={(match) => setSelectedMatchId(match.id)}
                onOpenMatch={handleOpenMatch}
                onOpenRadar={handleOpenRadar}
                t={t}
                totalCount={activeFilter === 'live' ? activeMatchCount : filteredMatches.length}
                activeFilter={activeFilter}
                visibleMetricIds={visibleMetricIds}
                onToggleMetric={handleToggleMetric}
                isColumnMenuOpen={isColumnMenuOpen}
                onToggleColumnMenu={() => setIsColumnMenuOpen((current) => !current)}
                onCloseColumnMenu={() => setIsColumnMenuOpen(false)}
                statWindow={statWindow}
              />
              <LiveMobileCards
                matches={filteredMatches}
                selectedMatch={selectedMatch}
                onSelectMatch={(match) => setSelectedMatchId(match.id)}
                onOpenMatch={handleOpenMatch}
                onOpenRadar={handleOpenRadar}
                activeFilter={activeFilter}
                statWindow={statWindow}
                visibleMetricIds={visibleMetricIds}
                t={t}
              />
            </>
          ) : (
            <div className="live-table-panel live-empty-state">{t('liveDashboard.empty', 'Sem partidas para este estado no momento.')}</div>
          )}

        </div>

          </>
        )}
      </div>
    </BaseLayout>
  )
}

export default LiveGames
